import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import DrawerFooter from '../drawer-components/DrawerFooter'
import InfoCard from '../drawer-components/InfoCard'
import {AddDrawerIntegrationProps} from '../utils/types'
import {
  createCloudflareIntegration,
  fetchAndSaveCloudflareData,
} from '../../../../Integrations/Cloudflare'
import {showLoadingToast, updateLoadingToast, dismissLoadingToast} from 'app/components/ShowToast'

const CloudflareDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'

  const [apiTokenName, setApiTokenName] = useState('')
  const [apiToken, setApiToken] = useState('')

  const getIntegration = React.useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Cloudflare') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              apiTokenName: data.apiTokenName,
              apiToken: data.apiToken,
            }
            setIntegration([integrationData])
            setApiTokenName(data.apiTokenName || '')
            setApiToken(data.apiToken ? '••••••••••••••••' : '')
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }, [id, currentUser?.tenantId])

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
    }
  }, [id, isOpen, getIntegration])

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    if (!apiTokenName || !apiToken) {
      showErrorToast({message: 'Please provide both API Token Name and API Token'})
      return
    }

    if (apiToken === '••••••••••••••••') {
      showErrorToast({message: 'Please enter a new API token'})
      return
    }

    setIsLoading(true)
    let toastId
    try {
      toastId = showLoadingToast({message: 'Validating Cloudflare API token...'})

      const cloudflareIntegration = createCloudflareIntegration(apiToken)
      updateLoadingToast(toastId, {
        message: 'Fetching Cloudflare data...',
        type: 'info',
        isLoading: true,
      })

      const syncResult = await cloudflareIntegration.fetchCloudflareData()

      if (!syncResult.success) {
        throw new Error(syncResult.error || 'Failed to fetch Cloudflare data')
      }

      updateLoadingToast(toastId, {
        message: 'Saving Cloudflare data...',
        type: 'info',
        isLoading: true,
      })

      const integrationId = await fetchAndSaveCloudflareData(
        apiTokenName,
        apiToken,
        syncResult.users || [],
        syncResult.domains || [],
        currentUser,
        (path: string, options?: {replace: boolean}) => {
          // Navigate function
          window.location.href = path
        }
      )

      if (!integrationId) {
        throw new Error('Failed to create integration')
      }

      dismissLoadingToast(toastId)
      showSuccessToast({message: 'Successfully connected to Cloudflare and retrieved data'})
      onClose()
      window.location.href = `/integrations?id=${integrationId}&service=Cloudflare`
    } catch (error: any) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to connect to Cloudflare'
      showErrorToast({message: errorMessage})
      dismissLoadingToast(toastId)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const cloudflareAssetsQuery = query(assetsRef, where('platform', '==', 'Cloudflare'))
      const cloudflareAssetsSnapshot = await getDocs(cloudflareAssetsQuery)

      const deleteAssetPromises = cloudflareAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const cloudflareVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Cloudflare'))
      const cloudflareVendorSnapshot = await getDocs(cloudflareVendorQuery)

      const deleteVendorPromises = cloudflareVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Cloudflare integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'Cloudflare integration disconnected'})

      setIntegration([])
      setApiTokenName('')
      setApiToken('')
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error disconnecting Cloudflare:', errorMessage)
      showErrorToast({message: 'Failed to disconnect Cloudflare'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Cloudflare' onClose={onClose} />

      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='Cloudflare'
            description='Centralized Authentication & Access Management Solution'
            logo='/integrations/cloudflare_Logo.avif'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will use your API token to read account members and domain (zone) data from Cloudflare.'
            accessTypeinfo="Requires a valid Cloudflare API token. Use the 'Read all resources' template when creating the token in Cloudflare Dashboard."
          />

          <>
            <div className='mb-8 d-flex justify-content-between align-items-center'>
              <div className='fs-6 fw-bold text-bold'>
                {isConnected
                  ? 'Cloudflare Connected to SecureSlate'
                  : 'Connect Cloudflare to SecureSlate'}{' '}
                <Overlay content='Upon successful connection, all account members and domains within your Cloudflare account will be automatically imported and presented within the Asset Repository section of the platform for your review and management.' />
              </div>
            </div>
            {!isConnected && (
              <InfoCard
                type='info'
                title='Connect Cloudflare to SecureSlate'
                description="Enter your Cloudflare API token name and API token below. Create an API token in Cloudflare Dashboard → My Profile → API Tokens with 'Use template' next to the Read all resources option. Once connected, account members and domains will be automatically fetched and configured."
              />
            )}

            <>
              <div className='mb-4'>
                <label className='form-label'>API Token Name</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Enter API token name (e.g., SecureSlateToken)'
                  value={apiTokenName}
                  onChange={(e) => setApiTokenName(e.target.value)}
                  disabled={isConnected}
                />
              </div>
              <div className='mb-4'>
                <label className='form-label'>API Token</label>
                <input
                  type='password'
                  className='form-control'
                  placeholder='Enter your Cloudflare API token'
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  disabled={isConnected}
                />
              </div>
            </>
          </>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default CloudflareDrawer
